
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useBooking } from '@/contexts/BookingContext';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';

// Mock staff for demonstration purposes
// In a real implementation, this would come from your database
const mockStaff = [
  {
    id: '1',
    name: 'Maria Schmidt',
    position: 'Senior Stylist',
    imageUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1061&q=80',
    specialties: ['Haarschnitt', 'Färben', 'Styling'],
  },
  {
    id: '2',
    name: 'Thomas Weber',
    position: 'Haar-Experte',
    imageUrl: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80',
    specialties: ['Herrenschnitt', 'Bart', 'Hochzeitsstyling'],
  },
  {
    id: '3',
    name: 'Sophie Müller',
    position: 'Coloristin',
    imageUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=988&q=80',
    specialties: ['Balayage', 'Ombre', 'Highlights'],
  },
];

const StaffStep = () => {
  const { selectedProvider, selectedService, selectedStaff, setSelectedStaff } = useBooking();
  const [isLoading, setIsLoading] = useState(true);
  const [availableStaff, setAvailableStaff] = useState(mockStaff);

  useEffect(() => {
    // In a real app, you would fetch staff for the selected provider and service
    // For now, we'll simulate loading with a timeout
    setIsLoading(true);
    const timer = setTimeout(() => {
      // Filter staff based on selectedProvider and selectedService
      // This is a placeholder for real backend logic
      setAvailableStaff(mockStaff.filter(() => true)); 
      setIsLoading(false);
    }, 600);
    
    return () => clearTimeout(timer);
  }, [selectedProvider, selectedService]);

  // Animation variants for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-6"
    >
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="overflow-hidden border border-white/10 bg-black/40 backdrop-blur-xl">
              <div className="aspect-square">
                <Skeleton className="h-full w-full bg-chrona-gray-800/60" />
              </div>
              <CardContent className="p-5">
                <Skeleton className="h-6 w-3/4 bg-chrona-gray-800/60 mb-2" />
                <Skeleton className="h-5 w-1/2 bg-chrona-gray-800/60 mb-3" />
                <Skeleton className="h-4 w-full bg-chrona-gray-800/60" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {availableStaff.map(staff => (
            <motion.div
              key={staff.id}
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <Card 
                className={`overflow-hidden cursor-pointer transition-all duration-300 border ${selectedStaff?.id === staff.id ? 'border-chrona-accent ring-1 ring-chrona-accent shadow-lg shadow-chrona-accent/20' : 'border-white/10'} bg-black/40 backdrop-blur-xl`}
                onClick={() => setSelectedStaff(staff)}
              >
                <div className="aspect-square">
                  <img 
                    src={staff.imageUrl} 
                    alt={staff.name} 
                    className="w-full h-full object-cover object-center" 
                  />
                </div>
                <CardContent className="p-5">
                  <h3 className="text-xl font-medium text-chrona-text mb-1">{staff.name}</h3>
                  <p className="text-sm text-chrona-accent mb-3">{staff.position}</p>
                  
                  {staff.specialties && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {staff.specialties.map(specialty => (
                        <span 
                          key={specialty} 
                          className="inline-block px-2 py-1 bg-chrona-gray-800/40 text-chrona-gray-300 text-xs rounded-full"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      {!isLoading && availableStaff.length === 0 && (
        <div className="text-center py-12">
          <p className="text-chrona-text text-lg mb-3">
            Keine Mitarbeiter für diesen Service verfügbar.
          </p>
          <p className="text-chrona-gray-400">
            Bitte wähle einen anderen Service oder kontaktiere den Anbieter direkt.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default StaffStep;
